import React, { useState } from 'react'

import Box from 'components_new/atoms/Box'
import Divider from 'components_new/atoms/Divider'
import Switch from 'components_new/atoms/Switch'
import Text from 'components_new/atoms/Text'

import GenericListItem from 'components_new/molecules/GenericListItem'
import { Option } from 'components_new/molecules/SelectSearch'
import SettingsEditDialog from 'components_new/molecules/SettingsEditDialog'
import SettingsGroup from 'components_new/molecules/SettingsGroup'
import SettingsItem from 'components_new/molecules/SettingsItem'
import SimpleTable from 'components_new/molecules/SimpleTable'

import PageLayout from 'components_new/templates/PageLayout'

import { translateTitle } from 'components_new/pages/internal/Users/utils'

import { useInternalAccountActions } from 'redux/actions/internal/Accounts'
import {
  useInternalAccount,
  useInternalAccountNps
} from 'redux/hooks/internal/Accounts'

import {
  InternalAccount,
  InternalNpsPeriod,
  UserTitle
} from 'types/GlobalInternalAccount'

import { getParsedDate } from 'utils/dateParser'
import { useParams } from 'utils/hooks'

import Loading from './Loading'

const TITLE_OPTIONS: Option[] = Object.values(UserTitle).map((title) => ({
  id: title,
  title: translateTitle[title]
}))

/**
 * The user's NPS history, with who the user is and the settings Homepal sets
 * on the account in the sidebar.
 */
const UserPage = () => {
  const { id } = useParams<{ id: string }>()

  const account = useInternalAccount(id)

  return (
    <PageLayout
      topbar={{
        breadcrumbs: [
          { title: 'Admin' },
          { href: '/internal/users', title: 'Våra användare' },
          { title: account?.full_name ?? '' }
        ]
      }}
      content={{ disablePadding: true, fullWidth: true }}
      rightSidebar={{
        content: account ? <Settings account={account} /> : null,
        size: 'medium'
      }}
      variant="feed"
    >
      {account ? <Feed account={account} /> : <Loading />}
    </PageLayout>
  )
}

const Settings = (props: { account: InternalAccount }) => {
  const { account } = props

  const { tryUpdateAccount } = useInternalAccountActions()

  const [titleDialog, setTitleDialog] = useState<{
    open: boolean
    value: string | null
  }>({ open: false, value: null })

  const handleCloseTitleDialog = () => {
    setTitleDialog((prev) => ({ ...prev, open: false }))
  }

  const handleSubmitTitle = () => {
    if (titleDialog.value) {
      tryUpdateAccount(account.id, {
        data: { title: titleDialog.value as UserTitle }
      })
    }

    handleCloseTitleDialog()
  }

  return (
    <>
      <SettingsGroup title={'Information'}>
        <SettingsItem title={'Email'} value={account.email} />
        <SettingsItem
          title={'Inbjuden'}
          value={getParsedDate(account.created_at)}
        />
        <SettingsItem
          title={'Accepterad'}
          value={
            account.invite_accepted_at
              ? getParsedDate(account.invite_accepted_at)
              : '–'
          }
        />
        <SettingsItem
          title={'Senast sedd'}
          value={
            account.last_seen_at
              ? getParsedDate(account.last_seen_at, undefined, { time: 'full' })
              : '–'
          }
        />
        <SettingsItem
          title={'Angiven roll'}
          value={account.additional_information.role ?? '–'}
        />
        <SettingsItem
          title={'Angiven preferens'}
          value={account.additional_information.preference ?? '–'}
        />
      </SettingsGroup>
      <SettingsGroup title={'Inställningar'}>
        <SettingsItem
          onClick={() =>
            setTitleDialog({ open: true, value: account.title ?? null })
          }
          title={'Roll'}
          value={account.title ? translateTitle[account.title] : null}
          variant="edit"
        />
        <SettingsItem
          actions={
            <Switch
              color="success"
              checked={account.is_in_management_group}
              onChange={() =>
                tryUpdateAccount(account.id, {
                  data: {
                    is_in_management_group: !account.is_in_management_group
                  }
                })
              }
              size="small"
            />
          }
          title={'Sitter i ledningsgrupp'}
        />
        <SettingsItem
          actions={
            <Switch
              color="success"
              checked={account.allow_early_access_export_tables}
              onChange={() =>
                tryUpdateAccount(account.id, {
                  data: {
                    allow_early_access_export_tables:
                      !account.allow_early_access_export_tables
                  }
                })
              }
              size="small"
            />
          }
          title={'Early Access: Tabeller'}
        />
      </SettingsGroup>
      <SettingsEditDialog
        onChange={(value) => setTitleDialog((prev) => ({ ...prev, value }))}
        onClose={handleCloseTitleDialog}
        onSubmit={handleSubmitTitle}
        open={titleDialog.open}
        title={'Roll'}
        type={'select'}
        value={titleDialog.value ?? ''}
        options={TITLE_OPTIONS}
      />
    </>
  )
}

const Feed = (props: { account: InternalAccount }) => {
  const { account } = props

  const periods = useInternalAccountNps(account.id)

  return (
    <Box>
      <GenericListItem
        body={account.customer.name}
        component="div"
        size="large"
        sx={{ mt: 2, p: 0, py: 1, width: 'auto' }}
        title={account.full_name}
      />
      <Divider sx={{ mb: 2, mt: 2 }} />
      <Text variant="h6" sx={{ mb: 2 }}>
        NPS
      </Text>
      <SimpleTable
        border={true}
        columns={[
          { title: 'Status' },
          { title: 'NPS' },
          { title: 'Feedback' },
          { title: 'Svarsdatum' },
          { title: 'Snoozes' }
        ]}
        disableSorting={true}
        emptyPlaceholder={'Inga svar eller snoozes'}
        loading={periods === null}
        rows={(periods ?? []).map((period: InternalNpsPeriod, i) => ({
          cells: [
            period.answer ? 'Svarat' : 'Snoozat',
            period.answer?.score ?? '–',
            period.answer?.response || '–',
            period.answer ? getParsedDate(period.answer.answered_at) : '–',
            period.snoozes.length > 0
              ? period.snoozes.map((snooze) => (
                <Box key={snooze.snoozed_at} sx={{ whiteSpace: 'nowrap' }}>
                  {getParsedDate(snooze.snoozed_at)} →{' '}
                  {getParsedDate(snooze.snooze_until)}
                </Box>
              ))
              : '–'
          ],
          id: String(i)
        }))}
        size="small"
      />
    </Box>
  )
}

export default UserPage
